<template>
  <listing-layout>
    <div slot="heading" class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">Secretary Users</h1>
      <router-link :to="{ name: 'Add Secretary Members' }" class="btn btn-primary d-md-none px-xl-5 mt-2 mt-sm-0">Add<span class="d-none d-sm-inline"> Secretary</span></router-link>
    </div>

    <div slot="content">
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>
      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>

      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div class="d-flex col-12 col-lg-auto px-0 mb-3 mb-lg-0">
          <label for="secretaryType-all" class="sr-only">Type</label>
          <div class="filter flex-wrap">
            <div class="form-check pl-0" v-for="tab in SECRETARY_LIST_TYPE_TABS" :key="tab.value">
              <input
                class="form-check-input"
                type="radio"
                name="secretaryType"
                :id="'secretaryType-' + tab.value"
                :value="tab.value"
                :checked="filters.type === tab.value"
                @change="setFilter('type', tab.value)"
              >
              <label class="form-check-label" :for="'secretaryType-' + tab.value">{{ tab.label }}</label>
            </div>
          </div>
        </div>
        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
          <label for="secretariesSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="secretariesSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
          <router-link :to="{ name: 'Add Secretary Members' }" class="btn btn-fz-sm btn-primary d-none d-md-block px-xl-5 mt-2 mt-sm-0 ml-md-0">Add<span class="d-inline"> Secretary</span></router-link>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No secretaries found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <list-name-cell align="right" :label="`${row.first_name} ${row.last_name}`" :to="profileRoute(row)">
              <template #badges>
                <span class="badge" :class="row.role === 'SecretaryOnly' ? 'alert-info' : 'alert-warning'">{{ row.role === 'SecretaryOnly' ? 'Secretary Only' : 'Member Secretary' }}</span>
                <span v-if="row.is_super_secretary" class="badge alert-success">Super Secretary</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:first_name="{ row }">
            <router-link :to="profileRoute(row)">{{ row.first_name }}</router-link>
          </template>
          <template #cell:last_name="{ row }">
            <list-name-cell align="right" :label="row.last_name" :to="profileRoute(row)">
              <template #badges>
                <span class="badge" :class="row.role === 'SecretaryOnly' ? 'alert-info' : 'alert-warning'">{{ row.role === 'SecretaryOnly' ? 'Secretary Only' : 'Member Secretary' }}</span>
                <span v-if="row.is_super_secretary" class="badge alert-success">Super Secretary</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:committee_name="{ row }">
            <router-link v-if="row.committee_id" :to="{ name: 'Committee Profile', params: { id: String(row.committee_id) } }">{{ row.committee_name }}</router-link>
          </template>
          <template #cell:phone="{ row }">{{ row.phone }}</template>

          <template #cell:login="{ row }">
            <button class="btn btn-sm btn-primary text-nowrap secretary-login-btn" :disabled="loggingInId === row.id" @click="login(row.id)">Login</button>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="secretariesShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="secretariesShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>
    </div>
  </listing-layout>
</template>

<script>
import { storeSuperSecretaryFlags } from '@/services/actingCommittee'
import axios from 'axios'
import { defineComponent, ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import ListNameCell from '@/components/ListNameCell'
import { SECRETARY_LIST_TYPE_TABS } from '@/constants/secretaryListType'
import useSecretariesList from '@/composables/secretaries/useSecretariesList'
import { buildColumns, buildBreakpoints } from './secretaryUsersColumns'

export default defineComponent({
  name: 'SecretaryUsers',
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    ListNameCell
  },
  setup() {
    const { proxy } = getCurrentInstance()

    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter
    } = useSecretariesList()

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()

    // A member secretary (role=Secretary, has their own personal account) profiles at
    // /members/:slug; a SecretaryOnly account (no personal member account) profiles at
    // /secretary-users/:slug — same conditional link the legacy page used.
    function profileRoute(row) {
      return row.role === 'Secretary'
        ? { name: 'Member Profile', params: { slug: row.id } }
        : { name: 'Secretary Profile', params: { slug: row.id } }
    }

    const searchInput = ref(filters.search || '')
    const successMessage = ref('')
    const errorMessage = ref('')

    // Search-as-you-type with a 500ms debounce; Enter / the icon fire it immediately.
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    // --- Login as secretary ---
    const loggingInId = ref(null)
    function login(id) {
      loggingInId.value = id
      axios.post(`${proxy.$api}/auth/login-as-user`, { id }).then((response) => {
        if (response.data.token) {
          localStorage.setItem('bearer', response.data.token)
          axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`
          localStorage.setItem('first_name', response.data.first_name)
          localStorage.setItem('last_name', response.data.last_name)
          localStorage.setItem('role', response.data.role)
          localStorage.setItem('userID', response.data.id)
          localStorage.setItem('is_onboarded', response.data.is_onboarded)
          storeSuperSecretaryFlags(response.data, { resetSelection: true })
          proxy.$router.push('/')
        }
      }).catch(() => {
        errorMessage.value = 'Unable to log in as this secretary. Please try again later.'
      }).finally(() => { loggingInId.value = null })
    }

    onMounted(() => fetch())

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      successMessage,
      errorMessage,
      setPage,
      setFilter,
      SECRETARY_LIST_TYPE_TABS,
      setSort,
      setPerPage,
      applySearch,
      profileRoute,
      loggingInId,
      login
    }
  }
})
</script>

<style scoped>
/* `.rtable__cell` is `display:flex; flex-direction:column` with the default `align-items:
   stretch` — a lone button as its only child stretches to the cell's full width by default
   (same mechanic that blew up the Financial Seasons badge on Members). The column's own
   `cellClass: 'text-center'` (secretaryUsersColumns.js) now overrides that to align-items:
   center, so the button centers instead of stretching — no align-self needed here any more.
   Padding/font-size shrunk to match the Horses list's Remove button (see .remove-btn there) —
   Bootstrap's plain .btn-sm is sized for a longer label than "Login". */
.secretary-login-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .secretary-login-btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Admin/Listing/SecretaryUsers.vue